import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useState, useRef, useEffect } from 'preact/hooks';
import { AlertCircle, Loader2, RefreshCw, Eye, EyeOff, Trash2 } from 'lucide-preact';
import { IntegrationProvider, IntegrationAuthType, INTEGRATION_PROVIDER_LABELS } from '@/types/integrations';
import { integrationsApi } from '@/api/integrations';
import { useToast } from '@/hooks/useToast';
import { ConfirmationModal } from '@/components';
import { CopyButton } from '@/components/common/CopyButton';
export function IntegrationPanel({ isOpen, workspaceId, selectedProvider, managingConnection, allProviders, onClose, onConnectionCreated, onConnectionUpdated, onConnectionDeleted, onConnectionArchived, }) {
    const [formValues, setFormValues] = useState({ Name: '', Description: '', ApiKey: '', Channel: '' });
    const [fieldErrors, setFieldErrors] = useState({});
    const [businessErrors, setBusinessErrors] = useState([]);
    const [createError, setCreateError] = useState(null);
    const [creating, setCreating] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [viewKeyVisible, setViewKeyVisible] = useState(false);
    const [viewingKey, setViewingKey] = useState(false);
    const [currentApiKey, setCurrentApiKey] = useState(null);
    const [viewKeyError, setViewKeyError] = useState(null);
    const [generatingKey, setGeneratingKey] = useState(false);
    const [rotatingKey, setRotatingKey] = useState(false);
    const [keyGenError, setKeyGenError] = useState(null);
    const nameInputRef = useRef(null);
    const cleanupRef = useRef(null);
    const { showSuccess, showInfo, showError } = useToast();
    const isManagingFormCarry = !!(managingConnection && (managingConnection.Provider || '').toLowerCase() === IntegrationProvider.Formcarry.toLowerCase());
    // Reset form when provider changes or when managing a different connection
    useEffect(() => {
        if (selectedProvider) {
            setFormValues({ Name: selectedProvider.title, Description: '', ApiKey: '', Channel: '' });
            setCreateError(null);
            setBusinessErrors([]);
            setFieldErrors({});
            setViewKeyVisible(false);
            setCurrentApiKey(null);
            setViewKeyError(null);
            setKeyGenError(null);
        }
    }, [selectedProvider]);
    useEffect(() => {
        if (managingConnection) {
            setFormValues({
                Name: managingConnection.Name || '',
                Description: managingConnection.Description || '',
                ApiKey: '',
                Channel: managingConnection.Channel || ''
            });
            setCreateError(null);
            setBusinessErrors([]);
            setFieldErrors({});
            setViewKeyVisible(false);
            setCurrentApiKey(null);
            setViewKeyError(null);
            setKeyGenError(null);
        }
    }, [managingConnection]);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
        };
    }, []);
    const handleClose = () => {
        // Cancel any in-flight requests
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
        // Reset all state
        setFormValues({ Name: '', Description: '', ApiKey: '', Channel: '' });
        setFieldErrors({});
        setBusinessErrors([]);
        setCreateError(null);
        setCreating(false);
        setUpdating(false);
        setArchiving(false);
        setDeleting(false);
        setShowDeleteConfirm(false);
        setViewKeyVisible(false);
        setViewingKey(false);
        setCurrentApiKey(null);
        setViewKeyError(null);
        setGeneratingKey(false);
        setRotatingKey(false);
        setKeyGenError(null);
        onClose();
    };
    const isBusinessError = (v) => !!v && typeof v === 'object' && 'Title' in v && 'Message' in v;
    const handlePanelError = (raw) => {
        setBusinessErrors([]);
        setCreateError(null);
        if (!raw)
            return;
        let parsed;
        try {
            parsed = JSON.parse(raw);
        }
        catch { /* swallow */ }
        if (Array.isArray(parsed) && parsed.every(isBusinessError)) {
            setBusinessErrors(parsed.map(p => {
                const be = p;
                return { Title: String(be.Title), Message: String(be.Message) };
            }));
        }
        else {
            setCreateError(raw);
        }
    };
    const validate = () => {
        const errs = {};
        const nameTrim = formValues.Name.trim();
        const descTrim = formValues.Description?.trim() || '';
        const apiKeyTrim = formValues.ApiKey.trim();
        const channelTrim = formValues.Channel.trim();
        if (!nameTrim)
            errs.Name = 'Name is required.';
        if (nameTrim.length > 100)
            errs.Name = 'Name exceeds maximum length (100).';
        if (descTrim.length > 500)
            errs.Description = 'Description exceeds maximum length (500).';
        if (selectedProvider && !apiKeyTrim)
            errs.ApiKey = 'API Key is required.';
        const providerForValidation = selectedProvider ? selectedProvider.id : managingConnection?.Provider;
        const isBigMarker = providerForValidation === IntegrationProvider.BigMarker;
        if (isBigMarker) {
            if (!channelTrim)
                errs.Channel = 'Channel Id is required when Provider=BigMarker.';
            else if (channelTrim.length > 100)
                errs.Channel = 'Channel Id exceeds maximum length of 100 characters.';
        }
        else if (channelTrim.length > 100) {
            errs.Channel = 'Channel Id exceeds maximum length of 100 characters.';
        }
        setFieldErrors(errs);
        return errs;
    };
    const createConnection = async () => {
        if (!workspaceId || !selectedProvider)
            return;
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            if (errs.Name && nameInputRef.current)
                nameInputRef.current.focus();
            setCreateError('Please fix the highlighted errors.');
            setBusinessErrors([]);
            return;
        }
        if (selectedProvider.id === IntegrationProvider.Formcarry && !formValues.ApiKey.trim()) {
            setCreateError('Generate an API key before creating the connection.');
            return;
        }
        setCreating(true);
        setCreateError(null);
        setBusinessErrors([]);
        setFieldErrors({});
        let cancelled = false;
        cleanupRef.current = () => {
            cancelled = true;
        };
        try {
            const payload = {
                WorkspaceId: workspaceId,
                Provider: selectedProvider.id,
                Name: formValues.Name.trim(),
                Description: formValues.Description?.trim() || undefined,
                AuthType: IntegrationAuthType.ApiKey,
                ApiKey: formValues.ApiKey.trim(),
                Channel: selectedProvider.id === IntegrationProvider.BigMarker ? formValues.Channel.trim() : undefined,
            };
            const res = await integrationsApi.createIntegrationConnection(payload);
            if (cancelled)
                return;
            if (res.error) {
                handlePanelError(res.error);
                return;
            }
            if (res.data) {
                onConnectionCreated(res.data);
                showSuccess('Integration Connected', 'The connection was created successfully.');
                handleClose();
            }
        }
        catch (error) {
            if (!cancelled) {
                setCreateError('Failed to create integration. Please try again.');
            }
        }
        finally {
            if (!cancelled) {
                setCreating(false);
                cleanupRef.current = null;
            }
        }
    };
    const hasManageChanges = () => {
        if (!managingConnection)
            return false;
        const nameChanged = formValues.Name.trim() !== (managingConnection.Name || '');
        const descChanged = formValues.Description.trim() !== (managingConnection.Description || '');
        const keyEntered = !!formValues.ApiKey.trim();
        const channelChanged = formValues.Channel.trim() !== (managingConnection.Channel || '').trim();
        return nameChanged || descChanged || keyEntered || channelChanged;
    };
    const updateConnection = async () => {
        if (!workspaceId || !managingConnection)
            return;
        const errs = validate();
        if (errs.Name && !formValues.Name.trim())
            delete errs.Name;
        if (Object.keys(errs).length > 0) {
            setFieldErrors(errs);
            setCreateError('Please fix the highlighted errors.');
            setBusinessErrors([]);
            return;
        }
        if (!hasManageChanges()) {
            setCreateError('No changes to save.');
            setBusinessErrors([]);
            return;
        }
        setUpdating(true);
        setCreateError(null);
        setBusinessErrors([]);
        let cancelled = false;
        cleanupRef.current = () => {
            cancelled = true;
        };
        try {
            const dto = {
                Id: managingConnection.Id,
                WorkspaceId: workspaceId,
                Name: formValues.Name.trim() !== (managingConnection.Name || '') ? formValues.Name.trim() : undefined,
                Description: formValues.Description.trim() !== (managingConnection.Description || '') ? formValues.Description.trim() : undefined,
                ApiKey: formValues.ApiKey.trim() ? formValues.ApiKey.trim() : undefined,
            };
            if (managingConnection.Provider === IntegrationProvider.BigMarker) {
                const currentChannel = (managingConnection.Channel || '').trim();
                const newChannel = formValues.Channel.trim();
                if (newChannel !== currentChannel) {
                    dto.Channel = newChannel || null;
                }
            }
            const res = await integrationsApi.updateIntegrationConnection(workspaceId, managingConnection.Id, dto);
            if (cancelled)
                return;
            if (res.error) {
                handlePanelError(res.error);
                return;
            }
            onConnectionUpdated(managingConnection.Id, {
                Name: dto.Name,
                Description: dto.Description,
                Channel: dto.Channel !== undefined ? dto.Channel : undefined,
                UpdatedUtc: new Date().toISOString(),
            });
            setFormValues(f => ({ ...f, ApiKey: '' }));
            showSuccess('Changes Saved', 'Your updates have been applied.');
        }
        catch (error) {
            if (!cancelled) {
                setCreateError('Failed to update integration. Please try again.');
            }
        }
        finally {
            if (!cancelled) {
                setUpdating(false);
                cleanupRef.current = null;
            }
        }
    };
    const toggleArchive = async () => {
        if (!workspaceId || !managingConnection)
            return;
        setArchiving(true);
        setCreateError(null);
        setBusinessErrors([]);
        let cancelled = false;
        cleanupRef.current = () => {
            cancelled = true;
        };
        try {
            const isArchived = managingConnection.IsArchived === true;
            const dto = {
                Id: managingConnection.Id,
                WorkspaceId: workspaceId,
                IsArchived: !isArchived,
            };
            const res = await integrationsApi.updateIntegrationConnection(workspaceId, managingConnection.Id, dto);
            if (cancelled)
                return;
            if (res.error) {
                handlePanelError(res.error);
                return;
            }
            onConnectionArchived(managingConnection.Id, !isArchived);
            if (!isArchived) {
                showInfo('Connection Archived', 'The connection was archived. You can restore it anytime.');
            }
            else {
                showSuccess('Connection Restored', 'The connection has been reactivated.');
            }
        }
        catch (error) {
            if (!cancelled) {
                showError('Archive Failed', 'Failed to archive connection.');
            }
        }
        finally {
            if (!cancelled) {
                setArchiving(false);
                cleanupRef.current = null;
            }
        }
    };
    const deleteConnection = async () => {
        if (!workspaceId || !managingConnection)
            return;
        setDeleting(true);
        setCreateError(null);
        setBusinessErrors([]);
        let cancelled = false;
        cleanupRef.current = () => {
            cancelled = true;
        };
        try {
            const res = await integrationsApi.deleteIntegrationConnection(workspaceId, managingConnection.Id);
            if (cancelled)
                return;
            if (res.error) {
                showError('Delete Failed', res.error);
                handlePanelError(res.error);
                return;
            }
            onConnectionDeleted(managingConnection.Id);
            showSuccess('Integration Deleted', 'The connection has been permanently removed.');
            handleClose();
        }
        catch (error) {
            if (!cancelled) {
                showError('Delete Failed', 'Failed to delete connection.');
            }
        }
        finally {
            if (!cancelled) {
                setDeleting(false);
                setShowDeleteConfirm(false);
                cleanupRef.current = null;
            }
        }
    };
    const fetchAndRevealCurrentKey = async () => {
        if (!workspaceId || !managingConnection)
            return;
        setViewKeyError(null);
        if (viewKeyVisible) {
            setViewKeyVisible(false);
            return;
        }
        if (!currentApiKey) {
            setViewingKey(true);
            let cancelled = false;
            cleanupRef.current = () => {
                cancelled = true;
            };
            try {
                const res = await integrationsApi.viewIntegrationConnectionKey(workspaceId, managingConnection.Id);
                if (cancelled)
                    return;
                if (res.error) {
                    setViewKeyError(res.error);
                    showError('View Key Failed', res.error);
                    return;
                }
                let keyVal;
                const data = res.data;
                if (data && typeof data === 'object' && 'key' in data) {
                    const maybeKey = data.key;
                    if (typeof maybeKey === 'string')
                        keyVal = maybeKey;
                }
                else if (typeof data === 'string') {
                    keyVal = data;
                }
                if (keyVal)
                    setCurrentApiKey(keyVal);
            }
            catch (error) {
                if (!cancelled) {
                    setViewKeyError('Failed to retrieve key');
                    showError('View Key Failed', 'Failed to retrieve key');
                }
            }
            finally {
                if (!cancelled) {
                    setViewingKey(false);
                    cleanupRef.current = null;
                }
            }
        }
        setViewKeyVisible(true);
    };
    const generateServerKey = async (forRotation) => {
        if (generatingKey || rotatingKey)
            return;
        setKeyGenError(null);
        if (forRotation) {
            setRotatingKey(true);
        }
        else {
            setGeneratingKey(true);
        }
        let cancelled = false;
        cleanupRef.current = () => {
            cancelled = true;
        };
        try {
            const res = await integrationsApi.generateIntegrationApiKey(IntegrationProvider.Formcarry);
            if (cancelled)
                return;
            if (res.error) {
                setKeyGenError(res.error);
                showError('Key Generation Failed', res.error);
                return;
            }
            let apiKeyVal;
            const data = res.data;
            if (data && typeof data === 'object' && 'apiKey' in data) {
                const maybe = data.apiKey;
                if (typeof maybe === 'string')
                    apiKeyVal = maybe;
            }
            if (!apiKeyVal) {
                setKeyGenError('Unexpected response format.');
                showError('Key Generation Failed', 'Unexpected response format.');
                return;
            }
            setFormValues(f => ({ ...f, ApiKey: apiKeyVal }));
            showSuccess(forRotation ? 'New Key Generated' : 'API Key Generated', forRotation ? 'Remember to Save Changes to rotate the key.' : 'Proceed to create the connection.');
        }
        catch (error) {
            if (!cancelled) {
                const errorMsg = 'Failed to generate key';
                setKeyGenError(errorMsg);
                showError('Key Generation Failed', errorMsg);
            }
        }
        finally {
            if (!cancelled) {
                if (forRotation) {
                    setRotatingKey(false);
                }
                else {
                    setGeneratingKey(false);
                }
                cleanupRef.current = null;
            }
        }
    };
    if (!isOpen)
        return null;
    const activeProviderMeta = selectedProvider;
    const providerMeta = managingConnection ? allProviders.find(p => p.id === managingConnection.Provider ||
        p.id.toLowerCase() === (managingConnection.Provider || '').toLowerCase()) : null;
    return (_jsxs("div", { className: "fixed inset-0 z-40", children: [_jsx("div", { className: "absolute inset-0 bg-slate-900/30 backdrop-blur-sm opacity-100 animate-fade-in", onClick: () => { if (!creating && !updating && !archiving && !deleting)
                    handleClose(); } }), _jsxs("div", { className: "absolute inset-y-0 right-0 w-full max-w-xl shadow-xl bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 flex flex-col animate-slide-in", children: [_jsxs("div", { className: "px-6 py-5 flex items-start justify-between gap-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/50", children: [_jsxs("div", { className: "flex items-center gap-3", children: [activeProviderMeta && (_jsx("div", { className: "w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-md flex items-center justify-center overflow-hidden", children: _jsx("img", { src: activeProviderMeta.logo, alt: activeProviderMeta.title, className: "max-h-8 max-w-8 object-contain" }) })), providerMeta && (_jsx("div", { className: "w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-md flex items-center justify-center overflow-hidden", children: _jsx("img", { src: providerMeta.logo, alt: providerMeta.title, className: "max-h-8 max-w-8 object-contain" }) })), _jsxs("div", { children: [_jsx("h4", { className: "text-slate-900 dark:text-white font-semibold text-lg leading-tight", children: activeProviderMeta ? `${activeProviderMeta.title} Connection` : (managingConnection?.Name || 'Manage Connection') }), _jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: activeProviderMeta ? 'Configure credentials to establish the connection.' : 'Connection details and maintenance.' })] })] }), _jsx("button", { onClick: handleClose, className: "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 text-sm", disabled: creating || updating || archiving || deleting, "aria-label": "Close panel", children: "Close" })] }), _jsxs("div", { className: "flex-1 overflow-y-auto px-6 py-6 space-y-6", children: [(businessErrors.length > 0 || createError) && (_jsx("div", { className: "p-3 rounded-md border text-sm bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 space-y-2", children: businessErrors.length > 0 ? (_jsx("ul", { className: "space-y-2", children: businessErrors.map((e, idx) => (_jsxs("li", { className: "flex items-start gap-2", children: [_jsx(AlertCircle, { className: "w-4 h-4 mt-0.5 shrink-0" }), _jsxs("div", { children: [_jsx("p", { className: "font-medium leading-snug", children: e.Title }), _jsx("p", { className: "text-[11px] leading-snug opacity-90", children: e.Message })] })] }, idx))) })) : (_jsxs("div", { className: "flex items-start gap-2", children: [_jsx(AlertCircle, { className: "w-4 h-4 mt-0.5 shrink-0" }), _jsx("span", { children: createError })] })) })), _jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "space-y-5", children: [_jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1", children: ["Connection Name", _jsx("span", { className: "text-red-500 ml-0.5", children: "*" })] }), _jsx("input", { ref: nameInputRef, value: formValues.Name, onInput: e => {
                                                            setFormValues(f => ({ ...f, Name: e.currentTarget.value }));
                                                            if (createError)
                                                                validate();
                                                        }, onBlur: () => validate(), maxLength: 110, className: `w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${fieldErrors.Name ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600'}`, placeholder: "Connection name", disabled: creating || updating || archiving || deleting, autoFocus: true, "aria-invalid": !!fieldErrors.Name, "aria-describedby": fieldErrors.Name ? 'integration-name-error' : undefined }), fieldErrors.Name && (_jsx("p", { id: "integration-name-error", className: "mt-1 text-xs text-red-600 dark:text-red-400", children: fieldErrors.Name }))] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1", children: "Description" }), _jsx("textarea", { value: formValues.Description, onInput: e => {
                                                            setFormValues(f => ({ ...f, Description: e.currentTarget.value }));
                                                            if (createError)
                                                                validate();
                                                        }, onBlur: () => validate(), rows: 3, maxLength: 520, className: `w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none ${fieldErrors.Description ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600'}`, placeholder: "Optional description", disabled: creating || updating || archiving || deleting, "aria-invalid": !!fieldErrors.Description, "aria-describedby": fieldErrors.Description ? 'integration-description-error' : undefined }), _jsxs("div", { className: "mt-1 flex items-center justify-between", children: [fieldErrors.Description && _jsx("p", { id: "integration-description-error", className: "text-xs text-red-600 dark:text-red-400", children: fieldErrors.Description }), _jsxs("p", { className: "text-[10px] text-slate-400 dark:text-slate-500 ml-auto", children: [formValues.Description.length, "/500"] })] })] }), activeProviderMeta && (_jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1", children: ["API Key", _jsx("span", { className: "text-red-500 ml-0.5", children: "*" })] }), _jsxs("div", { className: "flex gap-2", children: [_jsx("input", { type: activeProviderMeta.id === IntegrationProvider.Formcarry ? 'text' : 'password', value: formValues.ApiKey, readOnly: activeProviderMeta.id === IntegrationProvider.Formcarry, onInput: e => {
                                                                    if (activeProviderMeta.id !== IntegrationProvider.Formcarry) {
                                                                        setFormValues(f => ({ ...f, ApiKey: e.currentTarget.value }));
                                                                        if (createError)
                                                                            validate();
                                                                    }
                                                                }, onBlur: () => { if (activeProviderMeta.id !== IntegrationProvider.Formcarry)
                                                                    validate(); }, className: `flex-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${fieldErrors.ApiKey ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600'} ${activeProviderMeta.id === IntegrationProvider.Formcarry ? 'cursor-default' : ''}`, placeholder: activeProviderMeta.id === IntegrationProvider.Formcarry ? 'Click Generate' : 'Paste API key', disabled: creating || generatingKey, "aria-invalid": !!fieldErrors.ApiKey, "aria-describedby": fieldErrors.ApiKey ? 'integration-apikey-error' : undefined }), activeProviderMeta.id === IntegrationProvider.Formcarry && (_jsxs("button", { type: "button", onClick: () => generateServerKey(false), className: "px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 flex items-center gap-2 whitespace-nowrap disabled:opacity-50", disabled: creating || generatingKey, title: "Generate API key", children: [generatingKey ? _jsx(Loader2, { className: "w-4 h-4 animate-spin" }) : _jsx(RefreshCw, { className: "w-4 h-4" }), generatingKey ? 'Generating' : 'Generate'] }))] }), fieldErrors.ApiKey && (_jsx("p", { id: "integration-apikey-error", className: "mt-1 text-xs text-red-600 dark:text-red-400", children: fieldErrors.ApiKey })), keyGenError && (_jsx("p", { className: "mt-1 text-xs text-red-600 dark:text-red-400", children: keyGenError })), activeProviderMeta.id === IntegrationProvider.Formcarry && formValues.ApiKey && !fieldErrors.ApiKey && !keyGenError && workspaceId && (_jsxs("div", { className: "mt-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 relative", children: [_jsx("p", { className: "text-xs font-medium text-slate-700 dark:text-slate-300 mb-1", children: "Submission URL" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("code", { className: "flex-1 text-xs text-slate-600 dark:text-slate-400 break-all", children: `${import.meta.env.VITE_API_BASE_URL}/api/FormCarry/Submit?WorkspaceId=${workspaceId}&IngestKey=${formValues.ApiKey}` }), _jsx(CopyButton, { text: `${import.meta.env.VITE_API_BASE_URL}/api/FormCarry/Submit?WorkspaceId=${workspaceId}&IngestKey=${formValues.ApiKey}`, label: "URL" })] })] }))] })), activeProviderMeta?.id === IntegrationProvider.BigMarker && (_jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1", children: ["Channel Id", _jsx("span", { className: "text-red-500 ml-0.5", children: "*" })] }), _jsx("input", { type: "text", value: formValues.Channel, onInput: e => {
                                                            setFormValues(f => ({ ...f, Channel: e.currentTarget.value }));
                                                            if (createError)
                                                                validate();
                                                        }, onBlur: () => validate(), maxLength: 110, className: `w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${fieldErrors.Channel ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600'}`, placeholder: "BigMarker Channel Id", disabled: creating, "aria-invalid": !!fieldErrors.Channel, "aria-describedby": fieldErrors.Channel ? 'integration-channel-error' : undefined }), fieldErrors.Channel && (_jsx("p", { id: "integration-channel-error", className: "mt-1 text-xs text-red-600 dark:text-red-400", children: fieldErrors.Channel }))] })), managingConnection && (_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1", children: "Current API Key" }), _jsxs("div", { className: "flex gap-2 items-stretch", children: [_jsx("input", { type: viewKeyVisible ? 'text' : 'password', value: viewKeyVisible ? (currentApiKey || '') : '••••••••••', readOnly: true, placeholder: viewingKey ? 'Loading...' : undefined, className: "flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50", disabled: viewingKey || updating || archiving || deleting }), _jsx("button", { type: "button", onClick: fetchAndRevealCurrentKey, disabled: viewingKey || updating || archiving || deleting, className: "px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 flex items-center gap-2 disabled:opacity-50", children: viewingKey ? _jsx(Loader2, { className: "w-4 h-4 animate-spin" }) : viewKeyVisible ? _jsx(EyeOff, { className: "w-4 h-4" }) : _jsx(Eye, { className: "w-4 h-4" }) })] }), viewKeyError && _jsx("p", { className: "mt-1 text-xs text-red-600 dark:text-red-400", children: viewKeyError }), isManagingFormCarry && workspaceId && viewKeyVisible && currentApiKey && (_jsxs("div", { className: "mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 relative", children: [_jsx("p", { className: "text-xs font-medium text-slate-700 dark:text-slate-300 mb-1", children: "Current Submission URL" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("code", { className: "flex-1 text-xs text-slate-600 dark:text-slate-400 break-all", children: `${import.meta.env.VITE_API_BASE_URL}/api/FormCarry/Submit?WorkspaceId=${workspaceId}&IngestKey=${currentApiKey}` }), _jsx(CopyButton, { text: `${import.meta.env.VITE_API_BASE_URL}/api/FormCarry/Submit?WorkspaceId=${workspaceId}&IngestKey=${currentApiKey}`, label: "URL" })] })] }))] }), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1", children: ["New API Key ", _jsx("span", { className: "font-normal text-slate-400 dark:text-slate-500", children: "(optional)" })] }), _jsxs("div", { className: "flex gap-2 items-stretch", children: [_jsx("input", { type: (managingConnection.Provider === IntegrationProvider.Formcarry && formValues.ApiKey) ? 'text' : 'password', value: formValues.ApiKey, readOnly: managingConnection.Provider === IntegrationProvider.Formcarry, onInput: e => {
                                                                            if (managingConnection.Provider !== IntegrationProvider.Formcarry) {
                                                                                setFormValues(f => ({ ...f, ApiKey: e.currentTarget.value }));
                                                                            }
                                                                        }, className: `flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${managingConnection.Provider === IntegrationProvider.Formcarry ? 'cursor-default' : ''}`, placeholder: managingConnection.Provider === IntegrationProvider.Formcarry ? 'Click Generate to rotate' : 'Enter new key to rotate', disabled: updating || rotatingKey }), managingConnection.Provider === IntegrationProvider.Formcarry && (_jsxs("button", { type: "button", onClick: () => generateServerKey(true), className: "px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 flex items-center gap-2 whitespace-nowrap disabled:opacity-50", disabled: updating || rotatingKey, title: "Generate new API key", children: [rotatingKey ? _jsx(Loader2, { className: "w-4 h-4 animate-spin" }) : _jsx(RefreshCw, { className: "w-4 h-4" }), rotatingKey ? 'Generating' : 'Generate'] }))] }), _jsxs("p", { className: "mt-1 text-[11px] text-slate-500 dark:text-slate-400", children: ["Leave blank to keep the existing key. ", managingConnection.Provider === IntegrationProvider.Formcarry ? 'Click Generate to produce a new key, then Save Changes to rotate.' : 'Enter a new key to rotate.'] })] }), managingConnection.Provider === IntegrationProvider.BigMarker && (_jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1", children: ["Channel Id", _jsx("span", { className: "text-red-500 ml-0.5", children: "*" })] }), _jsx("input", { type: "text", value: formValues.Channel, onInput: e => setFormValues(f => ({ ...f, Channel: e.currentTarget.value })), onBlur: () => validate(), maxLength: 110, className: `w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${fieldErrors.Channel ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600'}`, placeholder: "BigMarker Channel Id", disabled: updating || archiving || deleting, "aria-invalid": !!fieldErrors.Channel, "aria-describedby": fieldErrors.Channel ? 'integration-channel-manage-error' : undefined }), fieldErrors.Channel && (_jsx("p", { id: "integration-channel-manage-error", className: "mt-1 text-xs text-red-600 dark:text-red-400", children: fieldErrors.Channel }))] }))] }))] }), managingConnection && (_jsxs("div", { className: "space-y-2 text-xs text-slate-500 dark:text-slate-400", children: [_jsx("h5", { className: "text-slate-700 dark:text-slate-200 font-semibold text-sm", children: "Metadata" }), _jsxs("ul", { className: "space-y-1", children: [_jsxs("li", { children: [_jsx("span", { className: "font-medium", children: "Provider:" }), " ", INTEGRATION_PROVIDER_LABELS[managingConnection.Provider || ''] || managingConnection.Provider] }), _jsxs("li", { children: [_jsx("span", { className: "font-medium", children: "Auth Type:" }), " API Key"] }), managingConnection.Channel && _jsxs("li", { children: [_jsx("span", { className: "font-medium", children: "Channel:" }), " ", managingConnection.Channel] }), _jsxs("li", { children: [_jsx("span", { className: "font-medium", children: "Created:" }), " ", managingConnection.CreatedUtc ? new Date(managingConnection.CreatedUtc).toLocaleString() : '—'] }), _jsxs("li", { children: [_jsx("span", { className: "font-medium", children: "Updated:" }), " ", managingConnection.UpdatedUtc ? new Date(managingConnection.UpdatedUtc).toLocaleString() : '—'] }), _jsxs("li", { children: [_jsx("span", { className: "font-medium", children: "Status:" }), " ", managingConnection.Status || 'Active'] })] })] })), !managingConnection && activeProviderMeta && (_jsxs("div", { className: "space-y-2 text-xs text-slate-500 dark:text-slate-400", children: [_jsxs("h5", { className: "text-slate-700 dark:text-slate-200 font-semibold text-sm", children: ["About ", activeProviderMeta.title] }), _jsx("p", { className: "leading-relaxed", children: activeProviderMeta.desc })] }))] })] }), _jsxs("div", { className: "px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-4 bg-slate-50/60 dark:bg-slate-800/40", children: [_jsx("div", { className: "flex items-center gap-3", children: managingConnection && (_jsxs(_Fragment, { children: [_jsxs("button", { type: "button", onClick: () => setShowDeleteConfirm(true), disabled: archiving || updating || deleting, className: "px-4 py-2 text-sm font-medium rounded-lg border border-red-300 dark:border-red-600 bg-white dark:bg-slate-800 text-red-600 dark:text-red-400 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 dark:hover:bg-red-900/30", children: [deleting && _jsx(Loader2, { className: "w-4 h-4 animate-spin" }), _jsx(Trash2, { className: "w-4 h-4" }), " Delete"] }), _jsxs("button", { type: "button", onClick: toggleArchive, disabled: archiving || updating || deleting, className: "px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-700", children: [archiving && _jsx(Loader2, { className: "w-4 h-4 animate-spin" }), managingConnection.IsArchived ? 'Unarchive' : 'Archive'] })] })) }), _jsxs("div", { className: "flex items-center gap-3 ml-auto", children: [_jsx("button", { type: "button", onClick: handleClose, className: "px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600", disabled: creating || updating || archiving || deleting, children: "Close" }), activeProviderMeta && (_jsxs("button", { type: "button", onClick: createConnection, className: "px-5 py-2 text-sm font-semibold rounded-lg bg-primary-600 hover:bg-primary-700 text-white flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed", disabled: creating || archiving || updating || deleting, children: [creating && _jsx(Loader2, { className: "w-4 h-4 animate-spin" }), " Create Connection"] })), managingConnection && (_jsxs("button", { type: "button", onClick: updateConnection, disabled: updating || archiving || deleting || !hasManageChanges(), className: `px-5 py-2 text-sm font-semibold rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${hasManageChanges() ? 'bg-primary-600 hover:bg-primary-700 text-white' : 'bg-slate-300 dark:bg-slate-600 text-slate-600 dark:text-slate-300'}`, children: [updating && _jsx(Loader2, { className: "w-4 h-4 animate-spin" }), " Save Changes"] }))] })] }), managingConnection && (_jsx(ConfirmationModal, { isOpen: showDeleteConfirm, title: "Delete Integration?", message: `This will permanently remove the '${managingConnection.Name || 'integration'}' connection. This action cannot be undone.`, type: "danger", confirmText: "Delete", cancelText: "Cancel", onConfirm: deleteConnection, onCancel: () => { if (!deleting)
                            setShowDeleteConfirm(false); }, loading: deleting }))] })] }));
}
